import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { paginatedSchema } from "../pagination";

import { organisationSchema } from "./organisations";

export const timeseriesAlarmListItemSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  name: z.string(),
  organisation: organisationSchema,
  created: lizardDateSchema,
  last_modified: lizardDateSchema.nullable(),
  active: z.boolean(),
  repeat_triggers: z.boolean(),
  snooze_sign_on: z.number(),
  snooze_sign_off: z.number(),
  comparison: z.string() /* < or >*/,
  relative_start: z.string().nullable(),
  relative_end: z.string().nullable(),
  messages: z.array(
    z.object({
      message: z.string(),
      contact_group: z.string(),
    })
  ),
  history: z.string().nullable(),
  thresholds: z.array(
    z.object({
      value: z.number(),
      warning_level: z.string(),
    })
  ),
  timeseries: z.string(),
  latest_trigger: z.object({
    id: z.number().nullable(),
    trigger_time: lizardDateSchema,
    value: z.number(),
    value_time: lizardDateSchema,
    warning_level: z.string().nullable(),
    threshold_value: z.number().nullable(),
    threshold_time: lizardDateSchema.nullable(),
    first_time: lizardDateSchema.nullable(),
    message_sent: z.boolean(),
  }),
});

export type TimeseriesAlarmListItem = z.infer<typeof timeseriesAlarmListItemSchema>;

export const timeseriesAlarmListSchema = paginatedSchema(timeseriesAlarmListItemSchema);
export type TimeseriesAlarmList = z.infer<typeof timeseriesAlarmListSchema>;

export const timeseriesAlarmSchema = timeseriesAlarmListItemSchema;
export type TimeseriesAlarm = z.infer<typeof timeseriesAlarmSchema>;

export function useTimeseriesAlarms(params = {}) {
  const url = combineUrlAndParams("/api/v4/timeseriesalarms/", params);
  return useParsedQuery<TimeseriesAlarmList>(
    ["lizard-api-v4", "timeseriesalarms", url],
    () => fetchWithError(url),
    (json: unknown) => timeseriesAlarmListSchema.parse(json)
  );
}

export function useTimeseriesAlarm(uuid: string) {
  return useParsedQuery<TimeseriesAlarm>(
    ["lizard-api-v4", "timeseriesalarms", uuid],
    () => fetchWithError(`/api/v4/timeseriesalarms/${uuid}/`),
    (json: unknown) => timeseriesAlarmSchema.parse(json)
  );
}

export function useMultipleTimeseriesAlarms(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "timeseriesalarms", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/timeseriesalarms/${uuid}/`),
        parseFunction: (json: unknown) => timeseriesAlarmSchema.parse(json),
      };
    })
  );
}
